import { useMutation, useQueryClient } from "@tanstack/react-query";
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLQuery } from "@aws-amplify/api";
import {
  ApprovalDecisionType,
  ApprovalStatus,
  CreateApprovalDecisionMutation,
  Transaction,
  UpdateApprovalDecisionMutation,
  UpdateTransactionMutation,
} from "../../API";
import {
  createApprovalDecision,
  updateApprovalDecision,
  updateTransaction,
} from "../../graphql/mutations";
import { approvalKeys } from "./query-keys";

type ApproveInput = {
  transactionId: string;
  reviewerUserId: string;
  authGroups: string[];
  authGroupsRead: string[];
};

export const useApproveTransaction = (
  bookkeepingYearId: string | undefined
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      transactionId,
      reviewerUserId,
      authGroups,
      authGroupsRead,
    }: ApproveInput) => {
      // Non-atomic: createApprovalDecision + updateTransaction.
      // On step 2 failure, attempt compensating soft-delete of the decision
      // (best effort). True atomicity tracked in backlog 999.2.
      const decisionResponse = await API.graphql<
        GraphQLQuery<CreateApprovalDecisionMutation>
      >(
        graphqlOperation(createApprovalDecision, {
          input: {
            decision: ApprovalDecisionType.approved,
            transactionId,
            reviewerUserId,
            decidedAt: new Date().toISOString(),
            authGroups,
            authGroupsRead,
          },
        })
      );
      const decisionId = decisionResponse.data?.createApprovalDecision?.id;

      try {
        await API.graphql<GraphQLQuery<UpdateTransactionMutation>>(
          graphqlOperation(updateTransaction, {
            input: {
              id: transactionId,
              approvalStatus: ApprovalStatus.approved,
            },
          })
        );
      } catch (error) {
        if (decisionId) {
          try {
            await API.graphql<GraphQLQuery<UpdateApprovalDecisionMutation>>(
              graphqlOperation(updateApprovalDecision, {
                input: {
                  id: decisionId,
                  deletedAt: new Date().toISOString(),
                },
              })
            );
          } catch {
            console.warn(
              `Orphan ApprovalDecision ${decisionId} for transaction ${transactionId} — manual cleanup needed`
            );
          }
        }
        throw error;
      }
    },
    onMutate: async ({ transactionId }) => {
      await queryClient.cancelQueries({
        queryKey: approvalKeys.inbox(bookkeepingYearId),
      });

      const previousInbox = queryClient.getQueryData<Transaction[]>(
        approvalKeys.inbox(bookkeepingYearId)
      );

      queryClient.setQueryData<Transaction[]>(
        approvalKeys.inbox(bookkeepingYearId),
        (old) => old?.filter((transaction) => transaction.id !== transactionId)
      );

      return { previousInbox };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(
        approvalKeys.inbox(bookkeepingYearId),
        context?.previousInbox
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: approvalKeys.inbox(bookkeepingYearId),
      });
      queryClient.invalidateQueries({
        queryKey: approvalKeys.pendingCount(bookkeepingYearId),
      });
    },
  });
};
